import Notiflix from 'notiflix';
import api from '../../services/api/api';
import { ptBRToDate } from '../../utils/formatt';
import { AttachmentRules, PreloadFormProps } from './dependent.include.types';

const getCitysOfState = async (
  state: string,
  notiflixClass = '.notiflix-city',
): Promise<{ title: string; value: string }[]> => {
  try {
    Notiflix.Block.circle(notiflixClass);
    const {
      data: { content },
    } = await api.get(`/address/list-of-cities?state=${state}`);
    return content.map(
      ({ city, ibgeCityCode }: { city: string; ibgeCityCode: string }) => ({
        title: city,
        value: ibgeCityCode,
      }),
    );
  } catch (err) {
    throw new Error(
      'Ops não conseguimos buscar as cidades para seleção. Por favor, tente novamente mais tarde.',
    );
  } finally {
    Notiflix.Block.remove(notiflixClass);
  }
};

const getAllParents = async (
  notiflixClass = '.notiflix-parents',
): Promise<{ title: string; value: string }[]> => {
  try {
    Notiflix.Block.circle(notiflixClass);
    const {
      data: { content },
    } = await api.get('/generic/relationship');
    return content.map(({ name, id }: { name: string; id: string }) => ({
      title: name,
      value: id,
    }));
  } catch (err) {
    throw new Error(
      'Ops... Não conseguimos buscar a lista de parentes do servidor. Por favor, tente novamente mais tarde.',
    );
  } finally {
    Notiflix.Block.remove(notiflixClass);
  }
};

const getAllStates = async (
  notiflixClass = '.notiflix-states',
  notiflixClassUf = '.notiflix-uf',
): Promise<{
  stateOptions: { title: string; value: string }[];
  ufOptions: { title: string; value: string }[];
}> => {
  try {
    Notiflix.Block.circle(notiflixClass);
    Notiflix.Block.circle(notiflixClassUf);
    const {
      data: { content },
    } = await api.get('/address/list-of-states');
    const stateOptions = [
      {
        title: '',
        value: '',
      },
      ...content.map(({ name, id }: { name: string; id: string }) => ({
        title: name,
        value: id,
      })),
    ];
    const ufOptions = [
      { title: '', value: '' },
      ...content.map(({ id }: { id: string }) => ({
        title: id,
        value: id,
      })),
    ];
    return { stateOptions, ufOptions };
  } catch (err) {
    throw new Error(
      'Ops não conseguimos buscar os estados para seleção. Por favor, tente novamente mais tarde.',
    );
  } finally {
    Notiflix.Block.remove(notiflixClass);
    Notiflix.Block.remove(notiflixClassUf);
  }
};

const getAllCountry = async (
  notiflixClass = '.notiflix-country',
): Promise<{ title: string; value: string }[]> => {
  try {
    Notiflix.Block.circle(notiflixClass);
    const {
      data: { content },
    } = await api.get('/address/list-of-countries');
    return [
      { title: '', value: '' },
      ...content.map(({ name, id }: { name: string; id: string }) => ({
        title: name,
        value: id,
      })),
    ];
  } catch (err) {
    throw new Error(
      'Ops não conseguimos buscar os países para seleção. Por favor, tente novamente mais tarde.',
    );
  } finally {
    Notiflix.Block.remove(notiflixClass);
  }
};

const getAllMaritalStatus = async (
  notiflixClass = '.notiflix-marital',
): Promise<{ title: string; value: string }[]> => {
  try {
    Notiflix.Block.circle(notiflixClass);
    const {
      data: { content },
    } = await api.get('/generic/list-of-marital-status');
    return [
      { title: '', value: '' },
      ...content.map(({ name, id }: { name: string; id: string }) => ({
        title: name,
        value: id,
      })),
    ];
  } catch (err) {
    throw new Error(
      'Ops... Não conseguimos buscar os estados civis para seleção. Por favor, tente novamente mais tarde.',
    );
  } finally {
    Notiflix.Block.remove(notiflixClass);
  }
};

const getAllPhisicalSex = async (
  notiflixClass = '.notiflix-phisical-sex',
): Promise<{ title: string; value: string }[]> => {
  try {
    Notiflix.Block.circle(notiflixClass);
    const {
      data: { content },
    } = await api.get('/generic/list-of-phisical-sex');
    return [
      { title: '', value: '' },
      ...content.map(({ name, id }: { name: string; id: string }) => ({
        title: name,
        value: id,
      })),
    ];
  } catch (err) {
    throw new Error(
      'Ops... Não conseguimos buscar a lista de seleção de sexo no servidor. Por favor, tente novamente mais tarde.',
    );
  } finally {
    Notiflix.Block.remove(notiflixClass);
  }
};

const getAllReasonForInclusion = async (
  notiflixClass = '.notiflix-reason-inclusion',
): Promise<{ title: string; value: string }[]> => {
  try {
    Notiflix.Block.circle(notiflixClass);
    const {
      data: { content },
    } = await api.get('/beneficiary/list-of-reasons-for-inclusion');
    return content.map(({ name, id }: { name: string; id: string }) => ({
      title: name,
      value: id,
    }));
  } catch (err) {
    throw new Error(
      'Ops... Não conseguimos buscar os motivos para inclusão para seleção. Por favor, tente novamente mais tarde.',
    );
  } finally {
    Notiflix.Block.remove(notiflixClass);
  }
};

const getAllAttachmentsTypes = async (): Promise<AttachmentRules[]> => {
  try {
    const DEPENDENT = 1;
    const {
      data: { content },
    } = await api.get(
      `/beneficiary-inclusion/attachment-rules?typeOfBeneficiary=${DEPENDENT}`,
    );
    return content as AttachmentRules[];
  } catch (err) {
    throw new Error(
      'Ops... Não conseguimos buscar as regras de anexo. Por favor, tente novamente mais tarde.',
    );
  }
};

const getNationalities = async (
  notiflixClass = '.notiflix-nationality',
): Promise<{ title: string; value: string }[]> => {
  try {
    Notiflix.Block.circle(notiflixClass);
    const {
      data: { content },
    } = await api.get(`/generic/list-of-nationality`);
    return [
      {
        title: '',
        value: '',
      },
      ...content.map(({ name, id }: { name: string; id: string }) => ({
        title: name,
        value: id,
      })),
    ];
  } catch (err) {
    throw new Error(
      'Ops não conseguimos buscar as cidades para seleção. Por favor, tente novamente mais tarde.',
    );
  } finally {
    Notiflix.Block.remove(notiflixClass);
  }
};

const preloadForm = async ({
  requestId,
  formRef,
  reasonForInclusionRef,
  parentesRef,
  nationalityRef,
  stateRef,
  cityRef,
  ufRef,
  phisicalSexRef,
  maritalRef,
  countryRef,
  setParents,
  setWeddingDate,
  setIsMarried,
  setBirthDate,
  setEffectiveDate,
  setRgDispatchDate,
  setIdOfOwnerRequiredByContract,
  setRequestHolderId,
  setCityOptions,
}: PreloadFormProps): Promise<void> => {
  if (!formRef.current) return;
  const { setFieldValue } = formRef.current;

  const {
    data: {
      content: [content],
    },
  } = await api.get(
    `/beneficiary-inclusion/consult-solicitation-for-editing?requestId=${requestId}`,
  );

  /** default setters of form */
  setFieldValue('dependentName', content.beneficiaryName || '');
  setFieldValue('email', content.email || '');
  setFieldValue('phone', content.DDDNumberPhone + content.telephone || '');
  setFieldValue('cpf', content.cpf || '');
  setFieldValue('rg', content.rg || '');
  setFieldValue('organ', content.issuingBodyDescriptionRg || '');
  setFieldValue('father', content.fatherName || '');
  setFieldValue('mother', content.mother || '');
  setFieldValue('passportNumber', content.passportId || '');
  setFieldValue('product', content.planId || '');
  setFieldValue('previousRequestId', content.requestId);
  setFieldValue('weddingDate', content.weddingDate);
  setFieldValue('observation', content.observation);

  /** custom setters of form */
  reasonForInclusionRef.current?.setValue('', content.motiveIncludeId);
  parentesRef.current?.setValue('', content.relationshipNumber);
  nationalityRef.current?.setValue('', content.nationalityCode);
  stateRef.current?.setValue('', content.initialsBirthState);
  countryRef.current?.setValue('', content.issuingCountryCode);
  ufRef.current?.setValue('', content.issuingAgencyState);
  phisicalSexRef.current?.setValue('', content.physicalSex);
  maritalRef.current?.setValue('', content.maritalStatus);

  setParents(content.relationshipNumber);

  getCitysOfState(content.initialsBirthState).then((cityOptions) => {
    setCityOptions(cityOptions);
    cityRef.current?.setValue(content.descriptionMunicipalityOfBirth);
  });

  setWeddingDate({
    value: content.weddingDate ? ptBRToDate(content.weddingDate) : null,
    error: '',
  });

  setIsMarried(['2', '7'].includes(content.maritalStatus));

  if (content.birthDate) {
    setBirthDate({
      value: ptBRToDate(content.birthDate),
      error: '',
    });
  }

  if (content.hiringDate) {
    setEffectiveDate({
      value: ptBRToDate(content.hiringDate),
      error: '',
    });
  }

  if (content.emissionDate) {
    setRgDispatchDate({
      value: ptBRToDate(content.emissionDate),
      error: '',
    });
  }

  if (content.nrSeqTitularContrato) {
    setIdOfOwnerRequiredByContract(content.nrSeqTitularContrato);
  }
  setRequestHolderId(content.nrSeqTitularSolicitacao);
};

export const Helper = {
  preloadForm,
  getCitysOfState,
  getAllParents,
  getAllStates,
  getAllCountry,
  getAllMaritalStatus,
  getAllPhisicalSex,
  getAllReasonForInclusion,
  getAllAttachmentsTypes,
  getNationalities,
};
